import { formatTicker, SingleNews } from '@api';
import { Tooltip } from '@design/components/tooltip/Tooltip';
import { NewsThumbnailSkeleton } from '@terminal/components/news-thumbnail-skeleton/NewsThumbnailSkeleton';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { ArticleDate } from '@terminal/widgets/news-widget/components/article-date/ArticleDate';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AssetLogoContainer } from '../asset-logo-container/AssetLogoContainer';
import { NewsBadges } from '../news-badges/NewsBadges';
import styles from './NewsThumbnail.module.scss';

export interface NewsThumbnailProp {
  newsData: SingleNews;
  highlighted?: string;
  showFullTimestamp?: boolean;
  onClick?: (singleNews: SingleNews) => void;
}

export const NewsThumbnail = ({
  newsData,
  highlighted,
  showFullTimestamp,
  onClick,
}: NewsThumbnailProp) => {
  const { t } = useTranslation([
    'news',
    'chart',
    'analyze',
    'countries',
    'watchlistManager',
  ]);
  const titleRef = useRef<HTMLDivElement>(null);
  const { id, title, published, body, entities, highlights, source } = newsData;

  const { data: entitiesData, isLoading } = useEntities({
    entityTags: entities,
  });
  const [hasEllipsis, setHasEllipsis] = useState(false);

  useEffect(() => {
    if (titleRef.current) {
      setHasEllipsis(
        titleRef.current.scrollWidth > titleRef.current.clientWidth
      );
    }
  }, [isLoading, title, titleRef.current]);

  if (isLoading) {
    return <NewsThumbnailSkeleton />;
  }

  const handleThumbnailClick = () => onClick?.(newsData);

  const genHighlightedText = (text: string, highlighted: string) => {
    const highlightedArr = highlighted?.toLowerCase().split(' ');
    const textArr = text.split(' ');

    return textArr.map((word, index) => {
      if (highlightedArr?.includes(word.toLowerCase())) {
        return (
          <mark key={`${word}-${index}`} data-testid="highlighted">
            {word}{' '}
          </mark>
        );
      } else {
        return <span key={`${word}-${index}`}>{word} </span>;
      }
    });
  };

  return (
    <button
      className={styles.thumbnailButton}
      data-testid={`news-thumbnail-${id}`}
      onClick={handleThumbnailClick}
    >
      <div className={styles.header}>
        <div className={styles.tags}>
          {!!entitiesData.length && (
            <Tooltip
              className={styles.tooltip}
              label={entitiesData.map(entity => {
                return (
                  <div className={styles.tooltipWrapper} key={entity.id}>
                    <span className={styles.entityTicker}>
                      {formatTicker(entity)}
                    </span>
                    <span
                      className={styles.entityShortName}
                      data-testid="entity-name"
                    >
                      {entity.name_short || entity.name_full || entity.name}
                    </span>
                  </div>
                );
              })}
            >
              <div className={styles.logosWrapper} data-testid="logos-wrapper">
                {entitiesData
                  .slice(0, 5)
                  .reverse()
                  .map(entity => {
                    return (
                      <AssetLogoContainer
                        className={styles.styledAssetLogo}
                        key={entity.id}
                        entity={entity}
                        withFallback={false}
                        size={20}
                      />
                    );
                  })}
              </div>
            </Tooltip>
          )}

          <NewsBadges highlights={highlights} entityTags={entities} />
        </div>
        <p className={styles.metadataWrapper}>
          <span className={styles.metaItem}>
            {/* i18next-extract-mark-context-next-line ["benzinga", "mtn", "dowjones"] */}
            <span data-testid="news-provider">
              {t('news:source', {
                context: source,
              })}
            </span>
          </span>
          <span className={styles.metaItem}>
            <ArticleDate
              date={published}
              applicableRelativeTime
              showFullTimestamp={showFullTimestamp}
              timezoneType="abbreviation"
              data-testid="publishing-time"
            />
          </span>
        </p>
      </div>

      <div className={styles.content}>
        {title && (
          <div className={styles.headline} ref={titleRef}>
            <Tooltip label={title} disabled={!hasEllipsis}>
              <span data-testid="headline">
                {highlighted ? genHighlightedText(title, highlighted) : title}
              </span>
            </Tooltip>
          </div>
        )}

        {body && (
          <div className={styles.body} data-testid="news-body">
            {highlighted ? genHighlightedText(body, highlighted) : body}
          </div>
        )}
      </div>
    </button>
  );
};
